import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTeam } from "contexts/TeamContext";
import { getTeamMembersByOrganizationId } from "../../../api/teamMemberApi";
import teamListStyles from "./TeamList.module.css";
import type { Team } from "../types";

export type TeamListProps = {
  teams: Team[];
  /** Receives each row; rows may include optional `status` / `email` from the API. */
  onRowClick?: (row: Team) => void;
};

export type TeamListRef = {
  refresh: () => Promise<void>;
};

const TeamList = forwardRef<TeamListRef, TeamListProps>((props, ref) => {
  const { teams, onRowClick } = props;
  const { organizationId, setTeams: setTeamUsersContext } = useTeam();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    console.log("debug teams: " + JSON.stringify(teams));
  }, [teams]);

  const fetchTeamUsers = useCallback(async () => {
    if (Array.isArray(teams)) {
      setIsLoading(false);
      return;
    }

    if (!organizationId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(false);
      const users = await getTeamMembersByOrganizationId(organizationId);

      if (setTeamUsersContext) {
        setTeamUsersContext(users);
      }
    } catch (error) {
      console.error("❌ Error fetching team users:", error);
      alert("Failed to load team users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, setTeamUsersContext, teams]);

  useEffect(() => {
    void fetchTeamUsers();
  }, [fetchTeamUsers]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: fetchTeamUsers,
    }),
    [fetchTeamUsers]
  );

  if (isLoading) {
    return <div className={teamListStyles.loading}>1 Loading team users...</div>;
  }

  const teamRows = Array.isArray(teams) ? teams : [];

  const activeTeams = teamRows.filter((user) => {
    const status = (user as Team & { status?: string }).status;
    return (
      status !== "deactivated" &&
      status !== "removed" &&
      status !== "invited" &&
      status !== "rejected"
    );
  });

  return (
    <>
      <div className="select-component__duration-section">
        {activeTeams.length > 0 &&
          activeTeams.map((team) => (
            <div key={team.id}>
              <button
                type="button"
                className={`strength-button select-component__duration-button two-part-button ${teamListStyles.rowButton}${onRowClick ? ` ${teamListStyles.rowButtonClickable}` : ""}`}
                onClick={() => onRowClick?.(team)}
              >
                <span>
                  {team.name || (team as Team & { email?: string }).email}
                </span>
              </button>
            </div>
          ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {activeTeams.length === 0 && (
          <div>
            <div
              style={{
                fontWeight: "bold",
                display: "flex",
                justifyContent: "center",
              }}
            >
              No teams
            </div>
            <p>Tap + to create your first team.</p>
          </div>
        )}
      </div>
    </>
  );
});

TeamList.displayName = "TeamList";

export default TeamList;
